/**
 * ModalErrorBanner.js
 *
 * FT-284: shared error banner for in-voyage data modals. Renders inline in
 * the modal content flow above <InlineTimeField>. NOT fixed-positioned.
 *
 * Replaces the floating "fixed top-4" banner that used to live at
 * UnderwayPhase.js:443-451 — that banner is deleted in FT-284 because every
 * data modal now owns its own boundary-error surface.
 *
 * Contract (see FT-284 brief §8):
 *   <ModalErrorBanner message="…" kind="error" onDismiss={() => …} />
 *
 *   - role="alert" + aria-live="assertive" → SR users hear the message.
 *   - data-testid="modal-error-banner" → stable Playwright selector.
 *   - kind defaults to 'error' (red palette); 'warning' uses amber.
 *   - onDismiss optional; omitted = no dismiss button.
 */

import React from 'react';

const ModalErrorBanner = ({ message, kind = 'error', onDismiss }) => {
  if (!message) return null;
  const palette = kind === 'warning'
    ? 'bg-amber-50 border-amber-300 text-amber-800'
    : 'bg-red-50 border-red-300 text-red-800';
  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="modal-error-banner"
      className={`flex items-start gap-2 rounded-md border px-4 py-3 text-sm ${palette}`}
    >
      <span className="flex-1">{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-xs underline"
        >
          dismiss
        </button>
      )}
    </div>
  );
};

export default ModalErrorBanner;
